// A page's list of shares, read from the relay and changed on the page — on
// /shares and on a group's page (byj-1wv.51.5). Each change made here — a
// rename, a delete, a leave — is counted, so an answer asked for before it is
// not taken: it would bring back what was deleted, or the title as it was
// (byj-fym); and of two reads out at once, a retry's and a poll's, only the
// later asked is taken. The list is read again every `pollMs`, one poll at a
// time, a tick while one is out skipped, so a slow answer never lands over a
// newer one; nothing is asked while the tab is hidden, and it is read once
// when it is looked at again (byj-1wv.75). /shares reads again only while a
// share listed is live, or not connected and may come back — a share whose
// sidecar never comes back is ended by the relay (sweepGoneShares), so that
// does not keep it asking for good — while a group's page reads again
// `always`: shares join a group from other people's machines.
import { useCallback, useEffect, useRef, useState } from "react";
import type { ShareSummary } from "./api";

const message = (err: unknown) => (err instanceof Error ? err.message : String(err));

/**
 * What `read` answers — the list itself, or what holds it, a group — with
 * `of` finding its shares and `put` giving them back changed. Each is the
 * same function from one render to the next (one of the module's, or a
 * `useCallback`): a new `read` is read afresh. `error` is what the first
 * read, a retry or a change could not do, or else what the last poll could
 * not, which the next poll that answers clears; the list stays as it was.
 */
export function useShareList<T>(
  read: () => Promise<T>,
  pollMs: number,
  of: (x: T) => ShareSummary[],
  put: (x: T, shares: ShareSummary[]) => T,
  always = false,
) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pollError, setPollError] = useState<string | null>(null);
  const edits = useRef(0);
  // each read numbered as it is asked: an answer is taken only when no read
  // was asked after it, nor a change made since
  const reads = useRef(0);
  const take = useCallback((x: T, asked: { edits: number; read: number }) => {
    if (edits.current !== asked.edits || reads.current !== asked.read) return false;
    setData(x);
    return true;
  }, []);
  const ask = () => ({ edits: edits.current, read: (reads.current += 1) });

  const edit = useCallback(
    (f: (ss: ShareSummary[]) => ShareSummary[]) => {
      edits.current += 1;
      setData((x) => x && put(x, f(of(x))));
    },
    [of, put],
  );
  const gone = useCallback((ids: string[]) => edit((ss) => ss.filter((s) => !ids.includes(s.id))), [edit]);
  const renamed = useCallback((id: string, title: string) => edit((ss) => ss.map((s) => (s.id === id ? { ...s, title } : s))), [edit]);
  const fail = useCallback((err: unknown) => setError(message(err)), []);

  /** The first read, and a retry: what it could not read is said. */
  const load = useCallback(async () => {
    setError(null);
    const asked = ask();
    try {
      if (take(await read(), asked)) setPollError(null);
    } catch (err) {
      setError(message(err));
    }
  }, [read, take]);
  useEffect(() => {
    void load();
  }, [load]);

  const watch = !!data && (always || of(data).some((s) => s.live || !s.ended_at));
  const polling = useRef(false);
  useEffect(() => {
    if (!watch) return;
    const poll = () => {
      if (polling.current || document.visibilityState === "hidden") return;
      polling.current = true;
      const asked = ask();
      read()
        .then(
          (x) => take(x, asked) && setPollError(null),
          (err) => reads.current === asked.read && setPollError(message(err)),
        )
        .finally(() => (polling.current = false));
    };
    const timer = setInterval(poll, pollMs);
    const shown = () => document.visibilityState === "visible" && poll();
    document.addEventListener("visibilitychange", shown);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", shown);
    };
  }, [watch, pollMs, read, take]);

  return { data, error: error ?? pollError, load, gone, renamed, fail };
}
